/**
 * Превью по теме в списке обращений: саммари из внутренней заметки или первое/последнее сообщение.
 * Логика согласована с `frontend/src/pages/Tickets.js` (getTooltipContentFromMessages / formatTooltipText).
 */

import { parseApiDateUtc } from '@/lib/api-date'
import { api } from '@/lib/api'
import type { TicketMessagePreviewRow } from '@/types/api'

const PAGE = 200

const messagePreviewCache = new Map<string, Promise<TicketMessagePreviewRow[]>>()

async function fetchTicketMessagesMerged(ticketId: string): Promise<TicketMessagePreviewRow[]> {
  const first = await api.ticketMessages(ticketId, { limit: PAGE, offset: 0 })
  const total = typeof first.total === 'number' ? first.total : (first.messages?.length ?? 0)
  const m1 = Array.isArray(first.messages) ? first.messages : []
  if (total <= PAGE) return m1

  const off = Math.max(0, total - PAGE)
  const second = await api.ticketMessages(ticketId, { limit: PAGE, offset: off })
  const m2 = Array.isArray(second.messages) ? second.messages : []
  const byId = new Map<string, TicketMessagePreviewRow>()
  for (const m of m2) byId.set(m.id, m)
  for (const m of m1) byId.set(m.id, m)
  return Array.from(byId.values()).sort((a, b) => {
    const ta = parseApiDateUtc(a.created_at)?.getTime() ?? 0
    const tb = parseApiDateUtc(b.created_at)?.getTime() ?? 0
    return ta - tb
  })
}

/** Кэш по ticketId, чтобы не дёргать API повторно при повторном наведении */
export function getTicketSubjectPreviewMessages(ticketId: string): Promise<TicketMessagePreviewRow[]> {
  let p = messagePreviewCache.get(ticketId)
  if (!p) {
    p = fetchTicketMessagesMerged(ticketId).catch((e) => {
      messagePreviewCache.delete(ticketId)
      throw e
    })
    messagePreviewCache.set(ticketId, p)
  }
  return p
}

type TooltipParts = {
  summaryMessage: TicketMessagePreviewRow | null
  firstMessage: TicketMessagePreviewRow | null
  lastMessage: TicketMessagePreviewRow | null
}

export function getTooltipContentFromMessages(messages: TicketMessagePreviewRow[]): TooltipParts {
  const nonInternal = (messages || []).filter((m) => !m.is_internal && (m.content || '').trim())
  const firstNonInternal = nonInternal[0] || null
  const lastNonInternal =
    nonInternal.length > 1 ? nonInternal[nonInternal.length - 1]! : firstNonInternal
  const summaryMsg =
    (messages || [])
      .slice()
      .reverse()
      .find(
        (m) => m.is_internal && (m.content || '').toLowerCase().includes('саммари'),
      ) || null
  return { summaryMessage: summaryMsg, firstMessage: firstNonInternal, lastMessage: lastNonInternal }
}

function getMessageSenderLabel(msg: TicketMessagePreviewRow | null): string {
  if (!msg) return ''
  const type = (msg.message_type || '').toLowerCase()
  if (type === 'customer') return 'Клиент'
  const name = (msg.sender_name || '').trim()
  return name ? `Оператор: ${name}` : 'Оператор'
}

function getSummaryAuthorLabel(summaryMsg: TicketMessagePreviewRow | null): string {
  if (!summaryMsg || !summaryMsg.content) return 'Саммари'
  const c = summaryMsg.content.toLowerCase()
  if (c.includes('саммари разговора') || c.includes('передача на оператора')) return 'Саммари (AI)'
  if (c.includes('саммари диалога') || c.includes('запрошено оператором')) return 'Саммари (оператор)'
  return 'Саммари'
}

const full = (s: string | null | undefined) => (s || '').trim()

function operatorPreviewText(msg: TicketMessagePreviewRow | null): string {
  if (!msg) return ''
  const type = (msg.message_type || '').toLowerCase()
  const content = full(msg.content)
  if (type === 'customer') {
    return full(msg.content_translated) || content
  }
  return content
}

export function formatSubjectPreviewTooltip(messages: TicketMessagePreviewRow[]): string | null {
  const data = getTooltipContentFromMessages(messages)
  if (data.summaryMessage && data.summaryMessage.content) {
    let text = data.summaryMessage.content.trim()
    const match =
      text.match(/\n?Саммари разговора:\s*\n?/i) ||
      text.match(/\n?=== Саммари диалога[\s\S]*?===\s*\n+/i)
    if (match && match[0]) {
      const after = text.indexOf(match[0]) + match[0].length
      text = text.substring(after).trim()
    }
    const author = getSummaryAuthorLabel(data.summaryMessage)
    const body = full(text) || data.summaryMessage.content.trim()
    return `${author}:\n${body}`
  }
  if (data.firstMessage && data.lastMessage) {
    const firstBody = operatorPreviewText(data.firstMessage)
    const firstFrom = getMessageSenderLabel(data.firstMessage)
    const lastFrom = getMessageSenderLabel(data.lastMessage)
    const lastBody =
      data.firstMessage === data.lastMessage ? '' : operatorPreviewText(data.lastMessage)
    if (!lastBody) return `Первое сообщение (${firstFrom}):\n${firstBody}`
    return `Первое сообщение (${firstFrom}):\n${firstBody}\n\nПоследнее сообщение (${lastFrom}):\n${lastBody}`
  }
  if (data.firstMessage) {
    const from = getMessageSenderLabel(data.firstMessage)
    return `Первое сообщение (${from}):\n${operatorPreviewText(data.firstMessage)}`
  }
  return null
}

/** Высота области превью в строках (включая пустые строки-разделители). */
export const CLIENT_MESSAGES_PREVIEW_MAX_LINES = 20

/**
 * Превью-режим «сообщения»: только сообщения клиента, начиная с первого,
 * без заголовков и пояснений, разделённые пустой строкой, обрезано до 20 строк.
 */
export function formatSubjectPreviewClientMessages(
  messages: TicketMessagePreviewRow[],
): string | null {
  const clientMessages = (messages || [])
    .filter(
      (m) =>
        !m.is_internal &&
        (m.message_type || '').toLowerCase() === 'customer' &&
        (m.content || '').trim(),
    )
    .slice()
    .sort((a, b) => {
      const ta = parseApiDateUtc(a.created_at)?.getTime() ?? 0
      const tb = parseApiDateUtc(b.created_at)?.getTime() ?? 0
      return ta - tb
    })
  if (!clientMessages.length) return null
  const text = clientMessages.map((m) => operatorPreviewText(m)).join('\n\n')
  const lines = text.split('\n')
  return lines.slice(0, CLIENT_MESSAGES_PREVIEW_MAX_LINES).join('\n')
}
